import type {
  PresentationOptionV2,
  PresentationSectionV2,
  PresentationSectionV2Option,
  PresentationPageOptionCategory,
  PresentationStyle,
  QuoteFile
} from 'paintscout'
import React, { useState, useRef, useMemo } from 'react'
import type { DialogProps, MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import {
  createStyles,
  withStyles,
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Input,
  Typography,
  makeStyles
} from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import {
  buildPresentationSectionFromOption,
  createPresentationNextOption,
  createPresentationNextSection,
  getDefaultPresentationImages,
  getPresentationNextSectionOptions,
  insertSectionOptionTitle,
  uuid
} from '@paintscout/util/builder'
import { CloseButton } from '../common'
import classnames from 'classnames'
import { PresentationImbedPreview } from '../../PresentationPreview'
import MenuIcon from '@material-ui/icons/Menu'
import AddIcon from '@material-ui/icons/Add'
import { PRESENTATION_LABEL } from '@paintscout/util/constants'

export interface PresentationSectionSelectDialogProps extends Omit<DialogProps, 'message' | 'title' | 'loading'> {
  classes?: DialogProps['classes'] & StyleClasses<typeof styles>
  onConfirm: (section: PresentationSectionV2) => void
  presentation: PresentationOptionV2
  onCancel?: () => void
}

const styles = (theme: Theme) =>
  createStyles({
    dialogPaper: {
      minHeight: 500
    },
    tileContainer: {
      display: 'flex',
      margin: theme.spacing(0, -1.5)
    },
    tileCol: {
      flex: '50%',
      maxWidth: '50%',
      margin: theme.spacing(0, 1.5)
    },
    menuItem: {
      display: 'block',
      textDecoration: 'none'
    },
    menuItemActive: {
      textDecoration: 'underline'
    },
    menuSpacer: {
      margin: theme.spacing(2, 0)
    },
    list: {},
    helpAlert: {
      '& p': {
        marginBottom: theme.spacing(1),
        '&:last-child': {
          marginBottom: 0
        }
      }
    },
    inlineIcon: {
      fontSize: '1.25rem',
      marginRight: 2,
      position: 'relative',
      top: 4
    }
  })

function PresentationSectionSelectDialog({
  open,
  classes,
  onConfirm,
  presentation,
  onCancel,
  ...props
}: PresentationSectionSelectDialogProps) {
  const { options } = useClientOptions()
  const [category, setCategory] = useState<PresentationPageOptionCategory | null>('banner')

  const [selectedSection, setSelectedSection] = useState<null | PresentationSectionV2Option>(null)
  // Dismissal is per-dialog-open, not persisted (#22683) — the X is the universal "close this"
  // affordance, and this dialog opens repeatedly every time a section is added. Permanently
  // hiding the help text on the first click, with no way to bring it back, surprised users who
  // expected the X to just close it for now.
  const [hideHelp, setHideHelp] = useState(false)
  const handleHideHelp = () => {
    setHideHelp(true)
  }

  const allSectionOptions = useMemo(() => getPresentationNextSectionOptions({ options }), [])
  const sectionOptions = allSectionOptions.filter((sectionOption) => sectionOption.categories.includes(category))

  const selected = !!selectedSection
  const images = useMemo(() => getDefaultPresentationImages({ options, presentation }), [])
  const style = presentation.advanced.style
  const halfwayThrough = Math.ceil(sectionOptions.length / 2)
  const sectionOptionsCol1 = sectionOptions.slice(0, halfwayThrough)
  const sectionOptionsCol2 = sectionOptions.slice(halfwayThrough, sectionOptions.length)

  const contentRef = useRef(null)
  const handleChangeCategory = (category: PresentationPageOptionCategory) => {
    setCategory(category)
    contentRef?.current.scrollTo(0, 0)
  }

  return (
    <Dialog
      open={open}
      classes={!selected ? { paper: classes.dialogPaper } : null}
      fullWidth
      maxWidth={!selected ? 'xl' : 'md'}
      {...props}
    >
      <DialogTitle rightContent={<CloseButton onCancel={onCancel} />}>
        {!selected ? `Add Section` : `Section Details`}
      </DialogTitle>
      <DialogContent ref={contentRef}>
        {!selected && (
          <Grid container>
            <Grid item xs={2}>
              <div>
                <MenuItem
                  className={classnames({
                    [classes.menuItem]: true,
                    [classes.menuItemActive]: category === 'banner'
                  })}
                  label={'Banner'}
                  value={'banner'}
                  onClick={(val) => handleChangeCategory(val)}
                />
                <MenuItem
                  className={classnames({
                    [classes.menuItem]: true,
                    [classes.menuItemActive]: category === 'about'
                  })}
                  label={'About'}
                  value={'about'}
                  onClick={(val) => handleChangeCategory(val)}
                />
                <MenuItem
                  className={classnames({
                    [classes.menuItem]: true,
                    [classes.menuItemActive]: category === 'feature'
                  })}
                  label={'Features'}
                  value={'feature'}
                  onClick={(val) => handleChangeCategory(val)}
                />
                <MenuItem
                  className={classnames({
                    [classes.menuItem]: true,
                    [classes.menuItemActive]: category === 'testimonial'
                  })}
                  label={'Testimonials'}
                  value={'testimonial'}
                  onClick={(val) => handleChangeCategory(val)}
                />
                <MenuItem
                  className={classnames({
                    [classes.menuItem]: true,
                    [classes.menuItemActive]: category === 'gallery'
                  })}
                  label={'Gallery'}
                  value={'gallery'}
                  onClick={(val) => handleChangeCategory(val)}
                />
                <MenuItem
                  className={classnames({
                    [classes.menuItem]: true,
                    [classes.menuItemActive]: category === 'team'
                  })}
                  label={'Team'}
                  value={'team'}
                  onClick={(val) => handleChangeCategory(val)}
                />
                <MenuItem
                  className={classnames({
                    [classes.menuItem]: true,
                    [classes.menuItemActive]: category === 'documents'
                  })}
                  label={'Documents'}
                  value={'documents'}
                  onClick={(val) => handleChangeCategory(val)}
                />
                <MenuItem
                  className={classnames({
                    [classes.menuItem]: true,
                    [classes.menuItemActive]: category === 'footer'
                  })}
                  label={'Footer'}
                  value={'footer'}
                  onClick={(val) => handleChangeCategory(val)}
                />
                <div className={classes.menuSpacer}></div>
                <MenuItem
                  className={classnames({
                    [classes.menuItem]: true,
                    [classes.menuItemActive]: category === 'text'
                  })}
                  label={'Text'}
                  value={'text'}
                  onClick={(val) => handleChangeCategory(val)}
                />
                <MenuItem
                  className={classnames({
                    [classes.menuItem]: true,
                    [classes.menuItemActive]: category === 'media'
                  })}
                  label={'Media'}
                  value={'media'}
                  onClick={(val) => handleChangeCategory(val)}
                />
                <MenuItem
                  className={classnames({
                    [classes.menuItem]: true,
                    [classes.menuItemActive]: category === 'mediaAndText'
                  })}
                  label={'Media and Text'}
                  value={'mediaAndText'}
                  onClick={(val) => handleChangeCategory(val)}
                />
                {/* Sherwin-Williams partner pages (#21311) — on for every account since #23966 */}
                <div className={classes.menuSpacer}></div>
                <MenuItem
                  className={classnames({
                    [classes.menuItem]: true,
                    [classes.menuItemActive]: category === 'sherwin-williams'
                  })}
                  label={'Sherwin-Williams'}
                  value={'sherwin-williams'}
                  onClick={(val) => handleChangeCategory(val)}
                />
                <div className={classes.menuSpacer}></div>
                <MenuItem
                  className={classnames({
                    [classes.menuItem]: true
                  })}
                  label={'Empty'}
                  onClick={() =>
                    setSelectedSection(
                      createPresentationNextSection({
                        sectionOption: {
                          type: 'empty',
                          title: 'New Section',
                          label: 'New Section',
                          blocks: []
                        }
                      })
                    )
                  }
                />
              </div>
            </Grid>
            <Grid item xs={10} className={classes.list}>
              <>
                {!hideHelp && (
                  <Alert variant={'outlined'} severity={'info'} onClose={handleHideHelp} className={classes.helpAlert}>
                    <Typography variant={'body1'}>
                      {`Each Section you add will become an item in the Navigation Menu. Your clients can access this menu
                      by clicking`}{' '}
                      <MenuIcon className={classes.inlineIcon} />{' '}
                      {`in the Navigation Bar located at the top
                      of the ${PRESENTATION_LABEL}.`}
                    </Typography>
                    <Typography variant={'body1'}>
                      You can add Blocks within a Section by clicking the <AddIcon className={classes.inlineIcon} />{' '}
                      icon at the top and bottom of the purple / blue Block outlines.
                    </Typography>
                  </Alert>
                )}
                <div className={classes.tileContainer}>
                  <div className={classes.tileCol}>
                    {sectionOptionsCol1.map((section) => {
                      return (
                        <SectionPreview
                          key={section.blocks[0].key}
                          setSelectedSection={setSelectedSection}
                          sectionOption={section}
                          images={images}
                          style={style}
                        />
                      )
                    })}
                  </div>
                  <div className={classes.tileCol}>
                    {sectionOptionsCol2.map((section) => {
                      return (
                        <SectionPreview
                          key={section.blocks[0].key}
                          setSelectedSection={setSelectedSection}
                          sectionOption={section}
                          images={images}
                          style={style}
                        />
                      )
                    })}
                  </div>
                </div>
              </>
            </Grid>
          </Grid>
        )}
        {selected && (
          <div>
            <Input
              autoFocus
              label={`Section Title`}
              value={selectedSection.label}
              onChange={(ev) =>
                setSelectedSection({ ...selectedSection, label: ev.target.value, title: ev.target.value })
              }
              fullWidth={true}
              required
              inputProps={{ maxLength: 20 }}
            />
          </div>
        )}
      </DialogContent>
      {selected && (
        <DialogActions
          leftButton={
            <Button onClick={() => setSelectedSection(null)} variant="text">
              {'< Back'}
            </Button>
          }
        >
          <Button
            onClick={() => {
              onConfirm(createPresentationNextSection({ sectionOption: insertSectionOptionTitle(selectedSection) }))
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}

export default withStyles(styles)(PresentationSectionSelectDialog)

const useStyles = makeStyles<Theme>((theme) => ({
  presPreview: {
    transform: 'scale(0.5)',
    transformOrigin: 'top left',
    width: '200%',
    pointerEvents: 'none'
  },
  sectionOption: {
    marginBottom: theme.spacing(3),
    '&:last-child': {
      marginBottom: 0
    },
    border: `1px solid ${theme.palette.grey[500]}`,
    cursor: 'pointer',
    overflow: 'hidden',
    '&:hover': {
      borderColor: theme.palette.grey[800]
    }
  },
  pageTile: {
    overflow: 'hidden',
    transition: 'transform 400ms ease',
    '&:hover': {
      transform: 'scale(1.05)'
    }
  }
}))

function SectionPreview(props: {
  sectionOption: PresentationSectionV2Option
  setSelectedSection: React.Dispatch<React.SetStateAction<PresentationSectionV2Option>>
  style: PresentationStyle
  images: QuoteFile[]
}) {
  const { setSelectedSection, sectionOption, style, images } = props
  const { options } = useClientOptions()
  const classes = useStyles(props)
  const [height, setHeight] = useState(0)

  /**
   * Memoized because every `key` here comes from `uuid()`. Rebuilt per render, the preview
   * presentation gets a fresh key on every parent state change, and PresentationImbedPreview's
   * iframe unmounts and remounts each time.
   *
   * buildPresentationSectionFromOption is the same builder the canvas drop handler and the Add panel
   * tiles use, so no surface can preview one thing and insert another (#22688). handleSelect below
   * hands this already-swapped section to `selectedSection`, which is how Confirm keeps the photos
   * the user saw.
   */
  const presentation: PresentationOptionV2 = useMemo(() => {
    const initPresentation = createPresentationNextOption({ options })

    return {
      ...initPresentation,
      key: uuid(),
      advanced: {
        ...initPresentation.advanced,
        style: style
      },
      pages: [
        {
          key: uuid(),
          label: 'Preview',
          sections: [buildPresentationSectionFromOption({ sectionOption, images })]
        }
      ]
    }
  }, [options, sectionOption, style, images])

  const handleSelect = () => {
    const [previewSection] = presentation.pages[0].sections

    setSelectedSection({
      ...previewSection,
      blocks: previewSection.blocks.map((block) => ({ ...block, key: uuid() }))
    })
  }

  return (
    <div onClick={handleSelect} className={classes.sectionOption}>
      <div className={classes.pageTile} style={{ height: height / 2 }}>
        <PresentationImbedPreview
          classes={{
            root: classnames({
              [classes.presPreview]: true
            })
          }}
          border={'none'}
          loadingStyle={'none'}
          presentation={presentation}
          preview={'none'}
          autoHeight
          autoHeightCallback={setHeight}
          hideNav
          hideFooter
          preventScroll
        />
      </div>
    </div>
  )
}

function MenuItem(args: {
  label: string
  value?: PresentationPageOptionCategory
  onClick: (value: PresentationPageOptionCategory) => void
  className: string
}) {
  const { label, value, onClick, className } = args
  return (
    <Button className={className} variant="text" onClick={() => onClick(value)}>
      {label}
    </Button>
  )
}
